<template>
  <MunicipalOrganizationView
    :organizations="organizations"
    :loading="loading"
    :error="error"
    :show-add-modal="modals.add.visible"
    :show-edit-modal="modals.edit.visible"
    :show-delete-modal="modals.delete.visible"
    :form-data="formData"
    :form-errors="formErrors"
    :delete-target="deleteTarget"
    @show-add-modal="showAddModal"
    @show-edit-modal="showEditModal"
    @confirm-delete="confirmDelete"
    @add-organization="handleAddOrganization"
    @edit-organization="handleEditOrganization"
    @delete-organization="handleDeleteOrganization"
    @close-add-modal="closeAddModal"
    @close-edit-modal="closeEditModal"
    @close-delete-modal="closeDeleteModal"
    @back-to-menu="handleBackToMenu"
    @table-action="handleTableAction"
    @update:form-data="updateFormData"
  />
</template>

<script setup>
import { ref, onMounted } from 'vue'
import MunicipalOrganizationView from '../components/municipalOrganization/MunicipalOrganizationView.vue'
import * as municipalOrganizationService from '@/services/systemSettings/municipalOrganizationService.js'

const emit = defineEmits(['back-to-menu'])

const organizations = ref([])
const loading = ref(false)
const error = ref(null)

const modals = ref({
  add: { visible: false },
  edit: { visible: false },
  delete: { visible: false }
})

const formData = ref({
  organizationName: ''
})
const formErrors = ref({})
const deleteTarget = ref(null)
const editingId = ref(null)


const resetForm = () => {
  formData.value = {
    organizationName: ''
  }
  formErrors.value = {}
  editingId.value = null
}

const updateFormData = (val) => {
  formData.value = val
}

const showAddModal = () => {
  resetForm()
  modals.value.add.visible = true
}

const showEditModal = (organization) => {
  resetForm()
  editingId.value = organization.id
  formData.value = { ...organization }
  modals.value.edit.visible = true
}

const confirmDelete = (organization) => {
  deleteTarget.value = organization
  modals.value.delete.visible = true
}

const closeAddModal = () => {
  resetForm()
  modals.value.add.visible = false
}

const closeEditModal = () => {
  resetForm()
  modals.value.edit.visible = false
}

const closeDeleteModal = () => {
  deleteTarget.value = null
  modals.value.delete.visible = false
}

const validateOrganizationData = (organizationData, isEdit = false) => {
  // 基本バリデーション
  const nameError = municipalOrganizationService.validateOrganizationName(organizationData.organizationName)
  if (nameError) {
    return nameError
  }

  // 重複チェック
  const excludeId = isEdit ? organizationData.id : null
  if (municipalOrganizationService.isDuplicateOrganizationName(
    organizationData.organizationName,
    organizations.value,
    excludeId
  )) {
    return 'この組織名は既に登録されています。別の名前を入力してください。'
  }

  return null
}

const handleAddOrganization = async (organizationData) => {
  loading.value = true
  error.value = null

  try {
    // フロントエンドバリデーション
    const validationError = validateOrganizationData(organizationData)
    if (validationError) {
      error.value = validationError
      return
    }

    const newOrganization = await municipalOrganizationService.createMunicipalOrganization(organizationData)
    organizations.value.push(newOrganization)

    closeAddModal()

  } catch (err) {
    error.value = err.message || '組織の追加に失敗しました'
    console.error('Add organization error:', err)
  } finally {
    loading.value = false
  }
}

const handleEditOrganization = async (organizationData) => {
  loading.value = true
  error.value = null

  try {
    // フロントエンドバリデーション（編集用）
    const validationError = validateOrganizationData(organizationData, true)
    if (validationError) {
      error.value = validationError
      return
    }

    const updatedOrganization = await municipalOrganizationService.updateMunicipalOrganization(
      organizationData.id,
      organizationData
    )

    const index = organizations.value.findIndex(org => org.id === organizationData.id)
    if (index !== -1) {
      organizations.value[index] = updatedOrganization

      closeEditModal()
    }

  } catch (err) {
    error.value = err.message || '組織の更新に失敗しました'
    console.error('Edit organization error:', err)
  } finally {
    loading.value = false
  }
}

const handleDeleteOrganization = async (organizationData) => {
  loading.value = true
  error.value = null

  try {
    await municipalOrganizationService.deleteMunicipalOrganization(organizationData.id)

    organizations.value = organizations.value.filter(org => org.id !== organizationData.id)

    closeDeleteModal()

  } catch (err) {
    error.value = err.message || '組織の削除に失敗しました'
    console.error('Delete organization error:', err)
  } finally {
    loading.value = false
  }
}

const handleBackToMenu = () => {
  emit('back-to-menu')
}

/**
 * テーブルアクションの統合ハンドラー
 * @param {Object} payload - { action: string, data: Object }
 */
const handleTableAction = (payload) => {
  const { action, data } = payload

  switch (action) {
    case 'edit':
      showEditModal(data)
      break
    case 'delete':
      confirmDelete(data)
      break
    default:
      console.warn('Unknown table action:', action)
  }
}

const fetchOrganizations = async () => {
  loading.value = true
  error.value = null

  try {
    const orgs = await municipalOrganizationService.getMunicipalOrganizations()
    organizations.value = orgs

  } catch (err) {
    error.value = err.message || '組織一覧の取得に失敗しました'
    console.error('Fetch organizations error:', err)
  } finally {
    loading.value = false
  }
}

onMounted(() => {
  fetchOrganizations()
})
</script>
